/**
 * Unified Item Service
 *
 * Single service for all item types (task, transaction, note, keyDate)
 * with support for cross-type sub-items.
 */
import {
  collection,
  collectionGroup,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  or,
  and,
  orderBy,
  onSnapshot,
  serverTimestamp,
  Timestamp,
  writeBatch,
  getDocs,
  getDoc,
  arrayUnion,
  arrayRemove
} from 'firebase/firestore';
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject
} from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { getFirebaseDb, getFirebaseStorage, getFirebaseFunctions } from './firebase';
import { handleAuthError } from './authRecoveryService';
import { deleteNotificationsByItemId, deleteNotificationsBySubItemId } from './notificationService';
import type {
  Item,
  SubItem,
  ItemType,
  Attachment,
  Reminder,
  Occurrence,
  CreateItemInput,
  UpdateItemInput,
  CreateSubItemInput,
  UpdateSubItemInput,
  UserRole,
} from '../types';
import { generateId, generateInitialOccurrences, updateOccurrencesOnEdit } from '../utils';

// ============================================================================
// COLLECTION HELPERS
// ============================================================================

function getItemsCollection(userId: string) {
  return collection(getFirebaseDb(), 'users', userId, 'items');
}

function getSubItemsCollection(userId: string, itemId: string) {
  return collection(getFirebaseDb(), 'users', userId, 'items', itemId, 'subItems');
}

function getItemRef(userId: string, itemId: string) {
  return doc(getFirebaseDb(), 'users', userId, 'items', itemId);
}

function getSubItemRef(userId: string, parentId: string, subItemId: string) {
  return doc(getFirebaseDb(), 'users', userId, 'items', parentId, 'subItems', subItemId);
}

// ============================================================================
// FIELD MAPPING
// ============================================================================

/**
 * Maps CreateItemInput to Firestore document data based on item type
 */
function mapInputToFirestoreData(
  input: CreateItemInput,
  userId: string
): Record<string, unknown> {
  const base: Record<string, unknown> = {
    type: input.type,
    name: input.name,
    description: input.description || '',
    category: input.category || 'None',
    subCategory: input.subCategory || 'None',
    itemGroup: input.itemGroup || 'None', // Scoped to category+subCategory
    organizationId: input.organizationId, // Required - validated in createItem()
    owner: input.owner || userId,
    createdBy: userId,
    userId,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    priority: input.priority || 'medium',
    attachments: [],
    // Defense-in-depth: strip reminders if no dueDate on non-recurring items
    reminders: (!input.dueDate && (!input.recurrence || input.recurrence === 'none'))
      ? []
      : (input.reminders || []),
    subItemCount: 0,
    completedSubItemCount: 0,
  };

  switch (input.type) {
    case 'task': {
      const taskData = {
        ...base,
        status: input.status || 'new',
        choreWheel: input.choreWheel || false,
        dueDate: input.dueDate ? Timestamp.fromDate(input.dueDate) : null,
        startDate: input.startDate ? Timestamp.fromDate(input.startDate) : null,
        endDate: input.endDate ? Timestamp.fromDate(input.endDate) : null,
        endDateForever: input.endDateForever || false,
        recurrence: input.recurrence || 'none',
        daysOfWeek: input.daysOfWeek || [],
        daysOfMonth: input.daysOfMonth || [],
        datesOfYear: input.datesOfYear || [],
        effortHours: input.effortHours,
        occurrences: [] as Occurrence[],
        completedAt: null,
        finalizedAt: null,
        urls: input.urls || [],
      };
      // Generate occurrences for recurring items
      if (taskData.recurrence !== 'none' && taskData.startDate) {
        taskData.occurrences = generateInitialOccurrences(taskData as unknown as Item);
      }
      return taskData;
    }

    case 'transaction': {
      const transactionData = {
        ...base,
        transactionType: input.transactionType || 'expense',
        amount: input.amount || 0,
        currency: input.currency || 'USD',
        entityId: input.entityId || 'None',
        accountId: input.accountId || 'None',
        status: input.status || 'draft',
        dueDate: input.dueDate ? Timestamp.fromDate(input.dueDate) : null,
        startDate: input.startDate ? Timestamp.fromDate(input.startDate) : null,
        endDate: input.endDate ? Timestamp.fromDate(input.endDate) : null,
        endDateForever: input.endDateForever || false,
        recurrence: input.recurrence || 'none',
        daysOfWeek: input.daysOfWeek || [],
        daysOfMonth: input.daysOfMonth || [],
        datesOfYear: input.datesOfYear || [],
        effortHours: input.effortHours,
        occurrences: [] as Occurrence[],
        completedAt: null,
        finalizedAt: null,
        urls: input.urls || [],
      };
      // Generate occurrences for recurring items
      if (transactionData.recurrence !== 'none' && transactionData.startDate) {
        transactionData.occurrences = generateInitialOccurrences(transactionData as unknown as Item);
      }
      return transactionData;
    }

    case 'note':
      return {
        ...base,
        content: input.content || '',
        color: input.color || 'default',
        tags: input.tags || [],
        status: input.status || 'draft',
        dueDate: input.dueDate ? Timestamp.fromDate(input.dueDate) : null,
        effortHours: input.effortHours,
        completedAt: null,
        finalizedAt: null,
        urls: input.urls || [],
      };

    case 'keyDate': {
      const keyDateData = {
        ...base,
        dueDate: input.dueDate ? Timestamp.fromDate(input.dueDate) : null,
        startDate: input.startDate ? Timestamp.fromDate(input.startDate) : null,
        endDate: input.endDate ? Timestamp.fromDate(input.endDate) : null,
        endDateForever: input.endDateForever || false,
        recurrence: input.recurrence || 'none',
        daysOfWeek: input.daysOfWeek || [],
        daysOfMonth: input.daysOfMonth || [],
        datesOfYear: input.datesOfYear || [],
        effortHours: input.effortHours,
        occurrences: [] as Occurrence[],
        status: input.status || 'draft',
        completedAt: null,
        finalizedAt: null,
      };
      // Generate occurrences for recurring items
      if (keyDateData.recurrence !== 'none' && keyDateData.startDate) {
        keyDateData.occurrences = generateInitialOccurrences(keyDateData as unknown as Item);
      }
      return keyDateData;
    }

    default:
      throw new Error(`Unknown item type: ${(input as CreateItemInput).type}`);
  }
}

/**
 * Maps CreateSubItemInput to Firestore document data
 */
function mapSubItemInputToFirestoreData(
  input: CreateSubItemInput,
  parentId: string,
  order: number,
  userId: string
): Record<string, unknown> {
  const base: Record<string, unknown> = {
    type: input.type,
    name: input.name,
    description: input.description || '',
    category: input.category || 'None',
    subCategory: input.subCategory || 'None',
    organizationId: input.organizationId, // Required - validated in createSubItem()
    owner: input.owner || userId,
    createdBy: userId,
    parentId,
    order,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    priority: input.priority || 'medium',
    attachments: [],
    // Defense-in-depth: sub-items are always non-recurring, strip reminders if no dueDate
    reminders: !input.dueDate ? [] : (input.reminders || []),
  };

  switch (input.type) {
    // SubItems: No choreWheel, no recurrence fields - only dueDate for scheduling
    case 'task':
      return {
        ...base,
        status: input.status || 'new',
        // choreWheel removed from sub-items
        dueDate: input.dueDate ? Timestamp.fromDate(input.dueDate) : null,
        effortHours: input.effortHours,
        completedAt: null,
        finalizedAt: null,
        urls: input.urls || [],
      };

    case 'transaction':
      return {
        ...base,
        transactionType: input.transactionType || 'expense',
        amount: input.amount || 0,
        currency: input.currency || 'USD',
        entityId: input.entityId || 'None',
        accountId: input.accountId || 'None',
        status: input.status || 'draft',
        dueDate: input.dueDate ? Timestamp.fromDate(input.dueDate) : null,
        effortHours: input.effortHours,
        completedAt: null,
        finalizedAt: null,
        urls: input.urls || [],
      };

    case 'note':
      return {
        ...base,
        content: input.content || '',
        color: input.color || 'default',
        tags: input.tags || [],
        status: input.status || 'draft',
        dueDate: input.dueDate ? Timestamp.fromDate(input.dueDate) : null,
        effortHours: input.effortHours,
        completedAt: null,
        finalizedAt: null,
        urls: input.urls || [],
      };

    case 'keyDate':
      return {
        ...base,
        dueDate: input.dueDate ? Timestamp.fromDate(input.dueDate) : null,
        effortHours: input.effortHours,
        status: input.status || 'draft',
        completedAt: null,
        finalizedAt: null,
      };

    default:
      throw new Error(`Unknown sub-item type: ${(input as CreateSubItemInput).type}`);
  }
}

/**
 * Maps UpdateItemInput to Firestore update data
 */
function mapUpdateToFirestoreData(
  input: UpdateItemInput,
  itemType: ItemType
): Record<string, unknown> {
  const updateData: Record<string, unknown> = {
    updatedAt: serverTimestamp(),
  };

  // Common fields
  if (input.name !== undefined) updateData.name = input.name;
  if (input.description !== undefined) updateData.description = input.description;
  if (input.category !== undefined) updateData.category = input.category;
  if (input.subCategory !== undefined) updateData.subCategory = input.subCategory;
  if ((input as { itemGroup?: string }).itemGroup !== undefined) {
    updateData.itemGroup = (input as { itemGroup?: string }).itemGroup;
  }
  if (input.owner !== undefined) updateData.owner = input.owner;
  // Always update organizationId if provided (fixes legacy null values)
  if ((input as { organizationId?: string }).organizationId !== undefined) {
    updateData.organizationId = (input as { organizationId?: string }).organizationId;
  }
  if (input.priority !== undefined) updateData.priority = input.priority;
  if (input.effortHours !== undefined) updateData.effortHours = input.effortHours;
  if (input.urls !== undefined) updateData.urls = input.urls;
  if ((input as { reminders?: Reminder[] }).reminders !== undefined) {
    const reminders = (input as { reminders?: Reminder[] }).reminders;
    // Defense-in-depth: strip reminders if dueDate is explicitly null and non-recurring
    const dueDateIsNull = input.dueDate === null || input.dueDate === undefined;
    const recurrenceIsNone = !input.recurrence || input.recurrence === 'none';
    updateData.reminders = (dueDateIsNull && recurrenceIsNone && reminders && reminders.length > 0)
      ? []
      : reminders;
  }
  if ((input as { lastKnownStatus?: string }).lastKnownStatus !== undefined) {
    updateData.lastKnownStatus = (input as { lastKnownStatus?: string }).lastKnownStatus;
  }

  // Type-specific fields
  switch (itemType) {
    case 'task':
      if (input.status !== undefined) updateData.status = input.status;
      if (input.choreWheel !== undefined) updateData.choreWheel = input.choreWheel;
      if (input.dueDate !== undefined) {
        updateData.dueDate = input.dueDate ? Timestamp.fromDate(input.dueDate) : null;
      }
      if (input.startDate !== undefined) {
        updateData.startDate = input.startDate ? Timestamp.fromDate(input.startDate) : null;
      }
      if (input.endDate !== undefined) {
        updateData.endDate = input.endDate ? Timestamp.fromDate(input.endDate) : null;
      }
      if (input.endDateForever !== undefined) updateData.endDateForever = input.endDateForever;
      if (input.recurrence !== undefined) updateData.recurrence = input.recurrence;
      if (input.daysOfWeek !== undefined) updateData.daysOfWeek = input.daysOfWeek;
      if (input.daysOfMonth !== undefined) updateData.daysOfMonth = input.daysOfMonth;
      if (input.datesOfYear !== undefined) updateData.datesOfYear = input.datesOfYear;
      // Handle completedAt: explicit value takes precedence, else auto-set based on status
      if (input.completedAt !== undefined) {
        updateData.completedAt = input.completedAt ? Timestamp.fromDate(input.completedAt) : null;
      } else if (input.status !== undefined) {
        // Auto-set based on status change
        updateData.completedAt = input.status === 'complete' ? serverTimestamp() : null;
        updateData.finalizedAt = null; // Tasks don't use finalizedAt
      }
      break;

    case 'transaction':
      if (input.status !== undefined) updateData.status = input.status;
      if (input.transactionType !== undefined) updateData.transactionType = input.transactionType;
      if (input.amount !== undefined) updateData.amount = input.amount;
      if (input.currency !== undefined) updateData.currency = input.currency;
      if (input.entityId !== undefined) updateData.entityId = input.entityId;
      if (input.accountId !== undefined) updateData.accountId = input.accountId;
      if (input.dueDate !== undefined) {
        updateData.dueDate = input.dueDate ? Timestamp.fromDate(input.dueDate) : null;
      }
      if (input.startDate !== undefined) {
        updateData.startDate = input.startDate ? Timestamp.fromDate(input.startDate) : null;
      }
      if (input.endDate !== undefined) {
        updateData.endDate = input.endDate ? Timestamp.fromDate(input.endDate) : null;
      }
      if (input.endDateForever !== undefined) updateData.endDateForever = input.endDateForever;
      if (input.recurrence !== undefined) updateData.recurrence = input.recurrence;
      if (input.daysOfWeek !== undefined) updateData.daysOfWeek = input.daysOfWeek;
      if (input.daysOfMonth !== undefined) updateData.daysOfMonth = input.daysOfMonth;
      if (input.datesOfYear !== undefined) updateData.datesOfYear = input.datesOfYear;
      // Handle finalizedAt: explicit value takes precedence, else auto-set based on status
      if (input.finalizedAt !== undefined) {
        updateData.finalizedAt = input.finalizedAt ? Timestamp.fromDate(input.finalizedAt) : null;
      } else if (input.status !== undefined) {
        // Auto-set based on status change
        updateData.finalizedAt = input.status === 'final' ? serverTimestamp() : null;
        updateData.completedAt = null; // Transactions don't use completedAt
      }
      break;

    case 'note':
      if (input.status !== undefined) updateData.status = input.status;
      if (input.content !== undefined) updateData.content = input.content;
      if (input.color !== undefined) updateData.color = input.color;
      if (input.tags !== undefined) updateData.tags = input.tags;
      if (input.aiSummary !== undefined) updateData.aiSummary = input.aiSummary;
      if (input.dueDate !== undefined) {
        updateData.dueDate = input.dueDate ? Timestamp.fromDate(input.dueDate) : null;
      }
      // Handle finalizedAt: explicit value takes precedence, else auto-set based on status
      if (input.finalizedAt !== undefined) {
        updateData.finalizedAt = input.finalizedAt ? Timestamp.fromDate(input.finalizedAt) : null;
      } else if (input.status !== undefined) {
        // Auto-set based on status change
        updateData.finalizedAt = input.status === 'final' ? serverTimestamp() : null;
      }
      break;

    case 'keyDate':
      if (input.status !== undefined) updateData.status = input.status;
      if (input.dueDate !== undefined) {
        updateData.dueDate = input.dueDate ? Timestamp.fromDate(input.dueDate) : null;
      }
      if (input.startDate !== undefined) {
        updateData.startDate = input.startDate ? Timestamp.fromDate(input.startDate) : null;
      }
      if (input.endDate !== undefined) {
        updateData.endDate = input.endDate ? Timestamp.fromDate(input.endDate) : null;
      }
      if (input.endDateForever !== undefined) updateData.endDateForever = input.endDateForever;
      if (input.recurrence !== undefined) updateData.recurrence = input.recurrence;
      if (input.daysOfWeek !== undefined) updateData.daysOfWeek = input.daysOfWeek;
      if (input.daysOfMonth !== undefined) updateData.daysOfMonth = input.daysOfMonth;
      if (input.datesOfYear !== undefined) updateData.datesOfYear = input.datesOfYear;
      // Handle finalizedAt: explicit value takes precedence, else auto-set based on status
      if (input.finalizedAt !== undefined) {
        updateData.finalizedAt = input.finalizedAt ? Timestamp.fromDate(input.finalizedAt) : null;
      } else if (input.status !== undefined) {
        // Auto-set based on status change
        updateData.finalizedAt = input.status === 'final' ? serverTimestamp() : null;
        updateData.completedAt = null; // KeyDates don't use completedAt
      }
      break;
  }

  return updateData;
}

/**
 * Maps UpdateSubItemInput to Firestore update data
 * SubItems don't have: choreWheel, recurrence, startDate, endDate, occurrences
 */
function mapSubItemUpdateToFirestoreData(
  input: UpdateSubItemInput,
  subItemType: ItemType
): Record<string, unknown> {
  const updateData: Record<string, unknown> = {
    updatedAt: serverTimestamp(),
  };

  // Common fields (same as items)
  if (input.name !== undefined) updateData.name = input.name;
  if (input.description !== undefined) updateData.description = input.description;
  if (input.category !== undefined) updateData.category = input.category;
  if (input.subCategory !== undefined) updateData.subCategory = input.subCategory;
  if (input.owner !== undefined) updateData.owner = input.owner;
  if ((input as { organizationId?: string }).organizationId !== undefined) {
    updateData.organizationId = (input as { organizationId?: string }).organizationId;
  }
  if (input.priority !== undefined) updateData.priority = input.priority;
  if (input.effortHours !== undefined) updateData.effortHours = input.effortHours;
  if (input.urls !== undefined) updateData.urls = input.urls;
  if ((input as { reminders?: Reminder[] }).reminders !== undefined) {
    const reminders = (input as { reminders?: Reminder[] }).reminders;
    // Defense-in-depth: sub-items are always non-recurring, strip reminders if no dueDate
    updateData.reminders = (!input.dueDate && reminders && reminders.length > 0)
      ? []
      : reminders;
  }
  if ((input as { lastKnownStatus?: string }).lastKnownStatus !== undefined) {
    updateData.lastKnownStatus = (input as { lastKnownStatus?: string }).lastKnownStatus;
  }

  // SubItem-specific: only dueDate, no recurrence fields
  if (input.dueDate !== undefined) {
    updateData.dueDate = input.dueDate ? Timestamp.fromDate(input.dueDate) : null;
  }

  // Type-specific fields (without recurrence)
  switch (subItemType) {
    case 'task':
      if (input.status !== undefined) updateData.status = input.status;
      // choreWheel not allowed on sub-items
      if (input.completedAt !== undefined) {
        updateData.completedAt = input.completedAt ? Timestamp.fromDate(input.completedAt) : null;
      } else if (input.status !== undefined) {
        updateData.completedAt = input.status === 'complete' ? serverTimestamp() : null;
        updateData.finalizedAt = null;
      }
      break;

    case 'transaction':
      if (input.status !== undefined) updateData.status = input.status;
      if (input.transactionType !== undefined) updateData.transactionType = input.transactionType;
      if (input.amount !== undefined) updateData.amount = input.amount;
      if (input.currency !== undefined) updateData.currency = input.currency;
      if (input.entityId !== undefined) updateData.entityId = input.entityId;
      if (input.accountId !== undefined) updateData.accountId = input.accountId;
      if (input.finalizedAt !== undefined) {
        updateData.finalizedAt = input.finalizedAt ? Timestamp.fromDate(input.finalizedAt) : null;
      } else if (input.status !== undefined) {
        updateData.finalizedAt = input.status === 'final' ? serverTimestamp() : null;
        updateData.completedAt = null;
      }
      break;

    case 'note':
      if (input.status !== undefined) updateData.status = input.status;
      if (input.content !== undefined) updateData.content = input.content;
      if (input.color !== undefined) updateData.color = input.color;
      if (input.tags !== undefined) updateData.tags = input.tags;
      if (input.aiSummary !== undefined) updateData.aiSummary = input.aiSummary;
      if (input.finalizedAt !== undefined) {
        updateData.finalizedAt = input.finalizedAt ? Timestamp.fromDate(input.finalizedAt) : null;
      } else if (input.status !== undefined) {
        updateData.finalizedAt = input.status === 'final' ? serverTimestamp() : null;
      }
      break;

    case 'keyDate':
      if (input.status !== undefined) updateData.status = input.status;
      if (input.finalizedAt !== undefined) {
        updateData.finalizedAt = input.finalizedAt ? Timestamp.fromDate(input.finalizedAt) : null;
      } else if (input.status !== undefined) {
        updateData.finalizedAt = input.status === 'final' ? serverTimestamp() : null;
      }
      break;
  }

  return updateData;
}

// ============================================================================
// ITEM CRUD OPERATIONS
// ============================================================================

/**
 * Create a new item
 */
export async function createItem(
  userId: string,
  input: CreateItemInput
): Promise<string> {
  if (!input.organizationId) {
    throw new Error('organizationId is required for item creation');
  }
  const itemsRef = getItemsCollection(userId);
  const data = mapInputToFirestoreData(input, userId);
  const docRef = await addDoc(itemsRef, data);
  return docRef.id;
}

/**
 * Update an existing item
 */
export async function updateItem(
  userId: string,
  itemId: string,
  input: UpdateItemInput
): Promise<void> {
  const itemRef = getItemRef(userId, itemId);

  // Get current item to know its type
  const itemDoc = await getDoc(itemRef);
  if (!itemDoc.exists()) {
    throw new Error(`Item ${itemId} not found`);
  }

  const currentData = itemDoc.data();
  const itemType = currentData.type as ItemType;
  const updateData = mapUpdateToFirestoreData(input, itemType);

  // Check if recurrence-related fields changed (for types that support recurrence)
  const recurrenceTypes: ItemType[] = ['task', 'transaction', 'keyDate'];
  if (recurrenceTypes.includes(itemType)) {
    const recurrenceFieldsChanged =
      input.recurrence !== undefined ||
      input.startDate !== undefined ||
      input.endDate !== undefined ||
      input.endDateForever !== undefined ||
      input.daysOfWeek !== undefined ||
      input.daysOfMonth !== undefined ||
      input.datesOfYear !== undefined;

    if (recurrenceFieldsChanged) {
      // Build updated item with merged fields for occurrence generation
      const mergedItem = {
        ...currentData,
        ...updateData,
        // Ensure dates are in correct format for recurrenceUtils
        startDate: updateData.startDate ?? currentData.startDate,
        endDate: updateData.endDate ?? currentData.endDate,
        recurrence: updateData.recurrence ?? currentData.recurrence,
      };

      // Only regenerate if item has/will have recurrence
      const recurrence = mergedItem.recurrence;
      if (recurrence && recurrence !== 'none') {
        const existingOccurrences = (currentData.occurrences || []) as Occurrence[];
        const updatedOccurrences = updateOccurrencesOnEdit(
          mergedItem as unknown as Item,
          existingOccurrences
        );
        updateData.occurrences = updatedOccurrences;
      } else {
        // Recurrence removed - clear future occurrences, keep past
        const existingOccurrences = (currentData.occurrences || []) as Occurrence[];
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        updateData.occurrences = existingOccurrences.filter((occ) => {
          // Handle Firestore Timestamp or plain Date
          const occDate = occ.date instanceof Date
            ? occ.date
            : typeof (occ.date as { toDate?: () => Date })?.toDate === 'function'
              ? (occ.date as { toDate: () => Date }).toDate()
              : new Date(occ.date as unknown as string | number);
          return occDate < today;
        });
      }
    }
  }

  await updateDoc(itemRef, updateData);
}

/**
 * Delete an item and all its sub-items, and clear associated notifications
 */
export async function deleteItem(
  userId: string,
  itemId: string
): Promise<void> {
  const db = getFirebaseDb();
  const batch = writeBatch(db);

  // Delete all sub-items first
  const subItemsQuery = query(getSubItemsCollection(userId, itemId));
  const subItemsSnapshot = await getDocs(subItemsQuery);
  subItemsSnapshot.forEach((subDoc) => {
    batch.delete(subDoc.ref);
  });

  // Delete the item
  batch.delete(getItemRef(userId, itemId));

  await batch.commit();

  // Permanently delete all notifications for this item (including sub-item notifications)
  await deleteNotificationsByItemId(itemId);
}

/**
 * Get a single item by ID
 */
export async function getItem(
  userId: string,
  itemId: string
): Promise<Item | null> {
  const itemRef = getItemRef(userId, itemId);
  const itemDoc = await getDoc(itemRef);

  if (!itemDoc.exists()) {
    return null;
  }

  return {
    id: itemDoc.id,
    ...itemDoc.data(),
  } as Item;
}

/**
 * Get all sub-items for an item (non-realtime)
 */
export async function getSubItems(
  userId: string,
  parentId: string
): Promise<SubItem[]> {
  const subItemsQuery = query(
    getSubItemsCollection(userId, parentId),
    orderBy('order', 'asc')
  );
  const snapshot = await getDocs(subItemsQuery);
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as SubItem[];
}

// ============================================================================
// SUBITEM CRUD OPERATIONS
// ============================================================================

/**
 * Create a new sub-item (supports cross-type sub-items)
 */
export async function createSubItem(
  userId: string,
  parentId: string,
  input: CreateSubItemInput
): Promise<string> {
  if (!input.organizationId) {
    throw new Error('organizationId is required for subItem creation');
  }
  const subItemsRef = getSubItemsCollection(userId, parentId);
  const parentRef = getItemRef(userId, parentId);

  // Calculate global order considering both attachments and sub-items
  const parentDoc = await getDoc(parentRef);
  const subItemsSnapshot = await getDocs(query(subItemsRef));

  let order = 0;
  if (parentDoc.exists()) {
    const existingAttachments = (parentDoc.data().attachments || []) as Attachment[];
    const maxAttachmentOrder = existingAttachments.reduce(
      (max, a) => Math.max(max, a.order ?? -1),
      -1
    );
    const maxSubItemOrder = subItemsSnapshot.docs.reduce(
      (max, doc) => Math.max(max, (doc.data().order ?? -1) as number),
      -1
    );
    order = Math.max(maxAttachmentOrder, maxSubItemOrder) + 1;
  }

  const data = mapSubItemInputToFirestoreData(input, parentId, order, userId);
  const docRef = await addDoc(subItemsRef, data);

  // Update parent sub-item count
  await updateDoc(parentRef, {
    subItemCount: subItemsSnapshot.size + 1,
    updatedAt: serverTimestamp(),
  });

  return docRef.id;
}

/**
 * Update an existing sub-item
 */
export async function updateSubItem(
  userId: string,
  parentId: string,
  subItemId: string,
  input: UpdateSubItemInput
): Promise<void> {
  const subItemRef = getSubItemRef(userId, parentId, subItemId);
  const parentRef = getItemRef(userId, parentId);

  // Get current sub-item to know its type
  const subItemDoc = await getDoc(subItemRef);
  if (!subItemDoc.exists()) {
    throw new Error(`SubItem ${subItemId} not found`);
  }

  const currentData = subItemDoc.data();
  const subItemType = currentData.type as ItemType;
  const updateData = mapSubItemUpdateToFirestoreData(input, subItemType);

  // SubItems don't support recurrence - no occurrence generation needed

  await updateDoc(subItemRef, updateData);

  // Update parent's completed count if status changed to complete
  if (input.status !== undefined) {
    const subItemsQuery = query(getSubItemsCollection(userId, parentId));
    const snapshot = await getDocs(subItemsQuery);
    const completedCount = snapshot.docs.filter((d) => {
      if (d.id === subItemId) {
        return input.status === 'complete';
      }
      return d.data().status === 'complete';
    }).length;

    await updateDoc(parentRef, {
      completedSubItemCount: completedCount,
      updatedAt: serverTimestamp(),
    });
  }
}

/**
 * Delete a sub-item
 */
export async function deleteSubItem(
  userId: string,
  parentId: string,
  subItemId: string
): Promise<void> {
  const subItemRef = getSubItemRef(userId, parentId, subItemId);
  const parentRef = getItemRef(userId, parentId);

  // Get current counts before deletion
  const subItemsQuery = query(getSubItemsCollection(userId, parentId));
  const snapshot = await getDocs(subItemsQuery);

  await deleteDoc(subItemRef);

  // Update parent counts
  const completedCount = snapshot.docs.filter(
    (d) => d.id !== subItemId && d.data().status === 'complete'
  ).length;

  await updateDoc(parentRef, {
    subItemCount: Math.max(0, snapshot.size - 1),
    completedSubItemCount: completedCount,
    updatedAt: serverTimestamp(),
  });

  // Permanently delete all notifications for this sub-item
  await deleteNotificationsBySubItemId(parentId, subItemId);
}

/**
 * Reorder sub-items
 */
export async function reorderSubItems(
  userId: string,
  parentId: string,
  orderedIds: string[]
): Promise<void> {
  const db = getFirebaseDb();
  const batch = writeBatch(db);

  orderedIds.forEach((subItemId, index) => {
    const subItemRef = getSubItemRef(userId, parentId, subItemId);
    batch.update(subItemRef, {
      order: index,
      updatedAt: serverTimestamp(),
    });
  });

  await batch.commit();
}

/**
 * Reorder item children (both attachments and sub-items together)
 * @param orderedItems Array of { type: 'attachment' | 'subItem', id: string } in desired order
 */
export async function reorderItemChildren(
  userId: string,
  itemId: string,
  orderedItems: Array<{ type: 'attachment' | 'subItem'; id: string }>
): Promise<void> {
  const db = getFirebaseDb();
  const itemRef = getItemRef(userId, itemId);

  // Get current item to update attachments
  const itemDoc = await getDoc(itemRef);
  if (!itemDoc.exists()) {
    throw new Error(`Item ${itemId} not found`);
  }

  const currentAttachments = (itemDoc.data().attachments || []) as Attachment[];

  // Build updated attachments array with new order values
  const updatedAttachments = currentAttachments.map((attachment) => {
    const orderIndex = orderedItems.findIndex(
      (item) => item.type === 'attachment' && item.id === attachment.id
    );
    return {
      ...attachment,
      order: orderIndex !== -1 ? orderIndex : attachment.order ?? 999,
    };
  });

  // Update sub-items with batch
  const batch = writeBatch(db);

  orderedItems.forEach((item, index) => {
    if (item.type === 'subItem') {
      const subItemRef = getSubItemRef(userId, itemId, item.id);
      batch.update(subItemRef, {
        order: index,
        updatedAt: serverTimestamp(),
      });
    }
  });

  // Update item with new attachment order
  batch.update(itemRef, {
    attachments: updatedAttachments,
    updatedAt: serverTimestamp(),
  });

  await batch.commit();
}

// ============================================================================
// SUBSCRIPTIONS (Real-time listeners)
// ============================================================================

/**
 * Subscribe to all items for an organization based on user role
 * - Admin: sees all items in the organization
 * - Standard: sees only items they created or are assigned as owner
 */
export function subscribeItems(
  userId: string,
  orgId: string,
  role: UserRole,
  callback: (items: Item[]) => void
): () => void {
  const db = getFirebaseDb();

  let itemsQuery;

  if (role === 'admin' || role === 'superAdmin') {
    // Admins see all org items
    itemsQuery = query(
      collectionGroup(db, 'items'),
      where('organizationId', '==', orgId),
      orderBy('createdAt', 'desc')
    );
  } else {
    // Standard users see items they created OR own
    itemsQuery = query(
      collectionGroup(db, 'items'),
      and(
        where('organizationId', '==', orgId),
        or(
          where('createdBy', '==', userId),
          where('owner', '==', userId)
        )
      )
    );
  }

  return onSnapshot(
    itemsQuery,
    (snapshot) => {
      const items = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as Item[];
      callback(items);
    },
    async (error) => {
      console.error('[ItemService] Items subscription error:', error);
      await handleAuthError(error);
    }
  );
}

/**
 * Subscribe to items of a specific type
 */
export function subscribeItemsByType<T extends Item>(
  userId: string,
  type: ItemType,
  callback: (items: T[]) => void
): () => void {
  const itemsQuery = query(
    getItemsCollection(userId),
    where('type', '==', type),
    orderBy('createdAt', 'desc')
  );

  return onSnapshot(
    itemsQuery,
    (snapshot) => {
      const items = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as T[];
      callback(items);
    },
    async (error) => {
      console.error(`[ItemService] ${type} subscription error:`, error);
      await handleAuthError(error);
    }
  );
}

/**
 * Subscribe to sub-items of a parent
 */
export function subscribeSubItems(
  userId: string,
  parentId: string,
  callback: (subItems: SubItem[]) => void
): () => void {
  const subItemsQuery = query(
    getSubItemsCollection(userId, parentId),
    orderBy('order', 'asc')
  );

  return onSnapshot(
    subItemsQuery,
    (snapshot) => {
      const subItems = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as SubItem[];
      callback(subItems);
    },
    async (error) => {
      console.error('[ItemService] SubItems subscription error:', error);
      await handleAuthError(error);
    }
  );
}

/**
 * Subscribe to sub-items of a specific type within a parent
 */
export function subscribeSubItemsByType<T extends SubItem>(
  userId: string,
  parentId: string,
  type: ItemType,
  callback: (subItems: T[]) => void
): () => void {
  const subItemsQuery = query(
    getSubItemsCollection(userId, parentId),
    where('type', '==', type),
    orderBy('order', 'asc')
  );

  return onSnapshot(
    subItemsQuery,
    (snapshot) => {
      const subItems = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as T[];
      callback(subItems);
    },
    async (error) => {
      console.error(`[ItemService] ${type} subItems subscription error:`, error);
      await handleAuthError(error);
    }
  );
}

// ============================================================================
// ATTACHMENT OPERATIONS
// ============================================================================

/**
 * Upload attachment to an item
 * Files are stored under the organization path for org-wide access
 * @param orgId - Organization ID (required for storage path)
 */
export async function uploadItemAttachment(
  userId: string,
  itemId: string,
  file: File,
  orgId: string
): Promise<Attachment> {
  const storage = getFirebaseStorage();
  const attachmentId = generateId();
  // Store under organization path for org-wide access
  const path = `organizations/${orgId}/attachments/items/${itemId}_${attachmentId}_${file.name}`;
  const storageRef = ref(storage, path);

  await uploadBytes(storageRef, file);
  const url = await getDownloadURL(storageRef);

  // Calculate order as max of existing attachments and sub-items + 1
  const itemRef = getItemRef(userId, itemId);
  const itemDoc = await getDoc(itemRef);
  let order = 0;
  if (itemDoc.exists()) {
    const existingAttachments = (itemDoc.data().attachments || []) as Attachment[];
    const maxAttachmentOrder = existingAttachments.reduce(
      (max, a) => Math.max(max, a.order ?? -1),
      -1
    );
    // Also check sub-items
    const subItemsSnapshot = await getDocs(query(getSubItemsCollection(userId, itemId)));
    const maxSubItemOrder = subItemsSnapshot.docs.reduce(
      (max, doc) => Math.max(max, (doc.data().order ?? -1) as number),
      -1
    );
    order = Math.max(maxAttachmentOrder, maxSubItemOrder) + 1;
  }

  const attachment: Attachment = {
    id: attachmentId,
    name: file.name,
    url,
    path,
    size: file.size,
    mimeType: file.type,
    uploadedAt: Timestamp.now(),
    order,
  };

  await updateDoc(itemRef, {
    attachments: arrayUnion(attachment),
    updatedAt: serverTimestamp(),
  });

  return attachment;
}

/**
 * Delete attachment from an item
 * Org membership is verified by storage rules via custom claims
 */
export async function deleteItemAttachment(
  userId: string,
  itemId: string,
  attachment: Attachment
): Promise<void> {
  const storage = getFirebaseStorage();
  const storageRef = ref(storage, attachment.path);

  await deleteObject(storageRef);

  const itemRef = getItemRef(userId, itemId);
  await updateDoc(itemRef, {
    attachments: arrayRemove(attachment),
    updatedAt: serverTimestamp(),
  });
}

/**
 * Upload attachment to a sub-item
 * Files are stored under the organization path for org-wide access
 * @param orgId - Organization ID (required for storage path)
 */
export async function uploadSubItemAttachment(
  userId: string,
  parentId: string,
  subItemId: string,
  file: File,
  orgId: string
): Promise<Attachment> {
  const storage = getFirebaseStorage();
  const attachmentId = generateId();
  // Store under organization path for org-wide access
  const path = `organizations/${orgId}/attachments/subitems/${parentId}_${subItemId}_${attachmentId}_${file.name}`;
  const storageRef = ref(storage, path);

  await uploadBytes(storageRef, file);
  const url = await getDownloadURL(storageRef);

  // Calculate order for sub-item attachments (within sub-item scope)
  const subItemRef = getSubItemRef(userId, parentId, subItemId);
  const subItemDoc = await getDoc(subItemRef);
  let order = 0;
  if (subItemDoc.exists()) {
    const existingAttachments = (subItemDoc.data().attachments || []) as Attachment[];
    const maxOrder = existingAttachments.reduce(
      (max, a) => Math.max(max, a.order ?? -1),
      -1
    );
    order = maxOrder + 1;
  }

  const attachment: Attachment = {
    id: attachmentId,
    name: file.name,
    url,
    path,
    size: file.size,
    mimeType: file.type,
    uploadedAt: Timestamp.now(),
    order,
  };

  await updateDoc(subItemRef, {
    attachments: arrayUnion(attachment),
    updatedAt: serverTimestamp(),
  });

  return attachment;
}

/**
 * Delete attachment from a sub-item
 * Org membership is verified by storage rules via custom claims
 */
export async function deleteSubItemAttachment(
  userId: string,
  parentId: string,
  subItemId: string,
  attachment: Attachment
): Promise<void> {
  const storage = getFirebaseStorage();
  const storageRef = ref(storage, attachment.path);

  await deleteObject(storageRef);

  const subItemRef = getSubItemRef(userId, parentId, subItemId);
  await updateDoc(subItemRef, {
    attachments: arrayRemove(attachment),
    updatedAt: serverTimestamp(),
  });
}

// ============================================================================
// CONVENIENCE OPERATIONS
// ============================================================================

/**
 * Toggle task status
 */
export async function toggleItemStatus(
  userId: string,
  itemId: string,
  status: 'new' | 'inProgress' | 'complete'
): Promise<void> {
  const itemRef = getItemRef(userId, itemId);
  const completedAt = status === 'complete' ? Timestamp.now() : null;

  await updateDoc(itemRef, {
    status,
    completedAt,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Add tag to note
 */
export async function addNoteTag(
  userId: string,
  noteId: string,
  tag: string
): Promise<void> {
  const noteRef = getItemRef(userId, noteId);
  await updateDoc(noteRef, {
    tags: arrayUnion(tag),
    updatedAt: serverTimestamp(),
  });
}

/**
 * Remove tag from note
 */
export async function removeNoteTag(
  userId: string,
  noteId: string,
  tag: string
): Promise<void> {
  const noteRef = getItemRef(userId, noteId);
  await updateDoc(noteRef, {
    tags: arrayRemove(tag),
    updatedAt: serverTimestamp(),
  });
}

// ============================================================================
// REMINDER OPERATIONS
// ============================================================================

/**
 * Add a reminder to an item
 */
export async function addReminder(
  userId: string,
  itemId: string,
  reminder: Omit<Reminder, 'id'>,
  subItemId?: string
): Promise<Reminder> {
  const fullReminder: Reminder = {
    id: generateId(),
    ...reminder,
  };

  if (subItemId) {
    const subItemRef = getSubItemRef(userId, itemId, subItemId);
    await updateDoc(subItemRef, {
      reminders: arrayUnion(fullReminder),
      updatedAt: serverTimestamp(),
    });
  } else {
    const itemRef = getItemRef(userId, itemId);
    await updateDoc(itemRef, {
      reminders: arrayUnion(fullReminder),
      updatedAt: serverTimestamp(),
    });
  }

  return fullReminder;
}

/**
 * Remove a reminder from an item
 */
export async function removeReminder(
  userId: string,
  itemId: string,
  reminderId: string,
  subItemId?: string
): Promise<void> {
  if (subItemId) {
    const subItemRef = getSubItemRef(userId, itemId, subItemId);
    const subItemDoc = await getDoc(subItemRef);
    if (subItemDoc.exists()) {
      const reminders = (subItemDoc.data().reminders || []) as Reminder[];
      const updatedReminders = reminders.filter((r) => r.id !== reminderId);
      await updateDoc(subItemRef, {
        reminders: updatedReminders,
        updatedAt: serverTimestamp(),
      });
    }
  } else {
    const itemRef = getItemRef(userId, itemId);
    const itemDoc = await getDoc(itemRef);
    if (itemDoc.exists()) {
      const reminders = (itemDoc.data().reminders || []) as Reminder[];
      const updatedReminders = reminders.filter((r) => r.id !== reminderId);
      await updateDoc(itemRef, {
        reminders: updatedReminders,
        updatedAt: serverTimestamp(),
      });
    }
  }
}

// ============================================================================
// OCCURRENCE EXTENSION (On-demand for calendar navigation beyond 180 days)
// ============================================================================

interface ExtendOccurrencesRequest {
  itemIds: string[];
  targetDate: string;
}

interface ExtendOccurrencesResponse {
  extended: number;
  message: string;
}

/**
 * Extend occurrences for recurring items when user navigates calendar beyond 180 days.
 * Calls the cloud function to generate and persist new occurrences.
 */
export async function extendItemOccurrences(
  itemIds: string[],
  targetDate: Date
): Promise<ExtendOccurrencesResponse> {
  try {
    const functions = getFirebaseFunctions();
    const extendFn = httpsCallable<ExtendOccurrencesRequest, ExtendOccurrencesResponse>(
      functions,
      'extendItemOccurrences'
    );

    const result = await extendFn({
      itemIds,
      targetDate: targetDate.toISOString(),
    });

    return result.data;
  } catch (error) {
    console.error('Error extending occurrences:', error);
    throw error;
  }
}

